import { range } from "@prismicio/editor-support/Array"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { pluralize } from "@prismicio/editor-support/String"
import { ErrorBoundaryContext } from "@prismicio/editor-support/Suspense"
import {
  AnimatedList,
  Badge,
  BlankSlate,
  BlankSlateActions,
  BlankSlateDescription,
  BlankSlateIcon,
  BlankSlateTitle,
  Box,
  Button,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  ErrorBoundary,
  FormSearchInput,
  IconButton,
  Skeleton,
  Text,
  useAlert,
  useToast,
} from "@prismicio/editor-ui"
import { Suspense, useContext, useState } from "react"
import { z } from "zod"

import {
  type EditorConfig,
  throwConfigError,
  useEditorConfig,
} from "../../../../../EditorConfig"
import { updateMediaCache } from "../../../hooks/mediaLibraryData"
import {
  maxTagNameLength,
  minTagNameLength,
  type Tag,
  tagNameSchema,
  updateTagCache,
  useTagSearch,
} from "../../../hooks/tagData"
import { useMediaLibrary } from "../../MediaLibraryContext"

export function SidepaneTagManager() {
  const { setTagManagerOpen } = useMediaLibrary()

  const [searchTerm, setSearchTerm] = useState("")

  return (
    <Box gap={20} flexDirection="column" height="100%">
      <Box alignItems="center" gap={4}>
        <IconButton
          icon="close"
          size="small"
          hasPadding={false}
          onClick={() => setTagManagerOpen(false)}
        />
        <Text variant="bold">Tags management</Text>
      </Box>
      <Box gap={16} flexDirection="column" height="100%">
        <FormSearchInput
          value={searchTerm}
          onValueChange={setSearchTerm}
          placeholder="Search for tags"
          endAdornment={false}
          autoFocus // eslint-disable-line jsx-a11y/no-autofocus -- we want the input to be focus once you open tag manager so focus is moved in sidepane
        />
        <ErrorBoundary renderError={() => <TagListError />}>
          <Suspense fallback={<TagListSkeleton />}>
            <TagList searchTerm={searchTerm} />
          </Suspense>
        </ErrorBoundary>
      </Box>
    </Box>
  )
}

interface TagListProps {
  searchTerm: string
}

function TagList(props: TagListProps) {
  const { searchTerm } = props

  const { tags } = useTagSearch()

  const filteredTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  if (filteredTags.length === 0) {
    return (
      <Text color="grey9" variant="small">
        No tags found
      </Text>
    )
  }

  return (
    <AnimatedList
      ariaLabel="Tags list"
      getItemKey={(tag) => tag.id}
      gap={16}
      items={filteredTags}
    >
      {(tag) => <MediaTag tag={tag} />}
    </AnimatedList>
  )
}

interface MediaTagProps {
  tag: Tag
}

function MediaTag(props: MediaTagProps) {
  const { tag } = props

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)

  const config = useEditorConfig()
  const { showAlert } = useAlert()
  const { showToast } = useToast()
  const { tags } = useTagSearch()

  async function onEdit(newTitle: string) {
    const isNotEligibleForTagName = !tagNameSchema.safeParse(newTitle).success
    if (isNotEligibleForTagName) {
      showAlert({
        title: "Error",
        subtitle: `Failed to rename tag "${tag.name}" to "${newTitle}". Tag name must be at least ${minTagNameLength} and at maximum ${maxTagNameLength} characters long.`,
      })
      return
    }

    const isNotUnique = tags.some(
      (existingTag) => existingTag.name === newTitle,
    )
    if (isNotUnique) {
      showAlert({
        title: "Error",
        subtitle: `Failed to rename tag "${tag.name}" to "${newTitle}". Tag with name "${newTitle}" already exists.`,
      })
      return
    }

    try {
      await renameTag({ id: tag.id, name: newTitle, config })

      updateTagCache((tags) =>
        tags.map((cachedTag) =>
          cachedTag.id === tag.id
            ? { ...cachedTag, name: newTitle }
            : cachedTag,
        ),
      )

      updateMediaCache((items) => {
        return items.map((item) => {
          const mediaHasTag =
            item.tags.findIndex((mediaTag) => mediaTag.id === tag.id) !== -1
          if (mediaHasTag) {
            const newTags = item.tags.map((mediaTag) =>
              mediaTag.id === tag.id
                ? { ...mediaTag, name: newTitle }
                : mediaTag,
            )
            return { ...item, tags: newTags }
          }
          return item
        })
      })

      showToast({
        title: `Tag "${tag.name}" renamed to "${newTitle}"`,
        seconds: 3,
      })
    } catch (_) {
      showAlert({
        title: "Error",
        subtitle: "Something went wrong when renaming the tag.",
      })
    }
  }

  return (
    <>
      <Box justifyContent="space-between">
        <Badge title={tag.name} color="purple" size="medium" onEdit={onEdit} />
        <Button
          invisible
          color="tomato"
          textWeight="normal"
          onClick={() => setDeleteConfirmationOpen(true)}
        >
          Delete
        </Button>
      </Box>
      <DeleteTagConfirmationModal
        open={deleteConfirmationOpen}
        onOpenChange={setDeleteConfirmationOpen}
        tag={tag}
      />
    </>
  )
}

interface DeleteTagConfirmationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tag: Tag
}

function DeleteTagConfirmationModal(props: DeleteTagConfirmationModalProps) {
  const { open, onOpenChange, tag } = props
  const { id, name, count } = tag

  const config = useEditorConfig()
  const { showAlert } = useAlert()
  const [loading, setLoading] = useState(false)

  async function onDelete() {
    setLoading(true)

    try {
      await deleteTag({ id, config })

      updateTagCache((tags) => tags.filter((cachedTag) => cachedTag.id !== id))

      updateMediaCache((items) => {
        return items.map((item) => {
          const mediaHasTag =
            item.tags.findIndex((mediaTag) => mediaTag.id === id) !== -1
          if (mediaHasTag) {
            const newTags = item.tags.filter((mediaTag) => mediaTag.id !== id)
            return { ...item, tags: newTags }
          }
          return item
        })
      })
    } catch (_) {
      showAlert({
        title: "Error",
        subtitle: "Something went wrong when removing the tag.",
      })
    } finally {
      setLoading(false)
      onOpenChange(false)
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open} size="small">
      <DialogHeader
        title={`This tag is being used on ${count} media ${pluralize(
          count,
          "item",
        )}`}
      />
      <DialogDescription asChild>
        <DialogContent padding={16}>
          <Text>
            You are about to delete the tag name <b>{name}</b>.{" "}
            {count > 0 &&
              `This tag is being used on ${count} media ${pluralize(
                count,
                "item",
              )} and will be deleted from ${pluralize(count, "it", "them")}.`}
          </Text>
        </DialogContent>
      </DialogDescription>
      <DialogActions>
        <DialogCancelButton />
        <DialogActionButton onClick={onDelete} color="tomato" loading={loading}>
          Yes, I want to delete
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

function TagListError() {
  const context = useContext(ErrorBoundaryContext)
  return (
    <Box flexDirection="column">
      <BlankSlate>
        <BlankSlateIcon
          name="close"
          lineColor="purple10"
          backgroundColor="purple3"
          borderColor="purple6"
          borderStyle="dashed"
        />
        <BlankSlateTitle size="big">Oops!</BlankSlateTitle>
        <BlankSlateDescription width={360}>
          We couldn't load your media tags.
        </BlankSlateDescription>
        <BlankSlateActions>
          <Button onClick={context?.reset} size="medium">
            Try again
          </Button>
        </BlankSlateActions>
      </BlankSlate>
    </Box>
  )
}

interface DeleteTagArgs {
  id: string
  config: EditorConfig
}

function deleteTag(args: DeleteTagArgs) {
  const { id, config } = args

  if (!config.mediaLibrary) throwConfigError("mediaLibrary")

  const { assetsApiBaseUrl, repository, authStrategy } = config

  return request(new URL(`tags/${id}`, assetsApiBaseUrl), {
    headers: { repository },
    method: "DELETE",
    credentials: getRequestCredentials(authStrategy),
    okType: z.unknown(),
  })
}

interface RenameTagArgs {
  id: string
  name: string
  config: EditorConfig
}

function renameTag(args: RenameTagArgs) {
  const { id, name, config } = args

  if (!config.mediaLibrary) throwConfigError("mediaLibrary")

  const { assetsApiBaseUrl, repository, authStrategy } = config

  return request(new URL(`tags/${id}`, assetsApiBaseUrl), {
    headers: { repository },
    method: "PATCH",
    credentials: getRequestCredentials(authStrategy),
    body: { name },
    okType: z.unknown(),
  })
}

function TagListSkeleton() {
  return (
    <Box flexDirection="column" gap={16}>
      {range(1, 6).map((index) => (
        <Box key={index} justifyContent="space-between">
          <Skeleton height={24} width={85} />
          <Skeleton height={24} width={45} />
        </Box>
      ))}
    </Box>
  )
}
